import * as THREE from 'three'
import type { SharedUniforms } from './AudioUniforms'
import { MAX_ZONES } from './shaderChunks'
import type { ZoneReactionConfig, ZoneShapeType } from '../types'

const SHAPE_ID: Record<ZoneShapeType, number> = {
  circle: 0,
  square: 1,
  triangle: 2,
  image: 3,
}

/** A single shape travelling the surface, tied to a rhythm band. */
interface Zone {
  /** Current position on the unit sphere (the surface direction). */
  dir: THREE.Vector3
  /** Rotation axis for travel (kept perpendicular to the start direction). */
  axis: THREE.Vector3
  /** Per-zone speed multiplier (used when speedRandom is on). */
  speedMul: number
  /** Rhythm band index (0 bass, 1 mid, 2 treble, 3 level). */
  band: number
}

/** Random unit vector (uniform on the sphere). */
function randomDir(): THREE.Vector3 {
  const z = Math.random() * 2 - 1
  const a = Math.random() * Math.PI * 2
  const r = Math.sqrt(1 - z * z)
  return new THREE.Vector3(Math.cos(a) * r, Math.sin(a) * r, z)
}

/** Structural signature: rebuild the zone list only when band/count change. */
function signature(cfg: ZoneReactionConfig): string {
  const bands = Math.max(1, Math.min(4, Math.round(cfg.bands)))
  const counts = cfg.perBand.slice(0, bands).map((b) => Math.round(b.count))
  return `${bands}|${counts.join(',')}`
}

/**
 * Zone reaction engine: owns the moving shapes ("zones") that ride the
 * primitive surface. Each frame it advances every zone along the sphere and
 * writes the zone uniforms consumed by the primitive shaders (ZONE_GLSL).
 * Shape type / size / band pulses are read live; only the zone *list* (counts
 * per band) is rebuilt on structural change. Owned by Scene.
 */
export class ZoneReaction {
  private uniforms: SharedUniforms
  private zones: Zone[] = []
  private sig = ''
  private loader = new THREE.TextureLoader()

  constructor(uniforms: SharedUniforms) {
    this.uniforms = uniforms
  }

  private rebuild(cfg: ZoneReactionConfig) {
    this.zones = []
    const bands = Math.max(1, Math.min(4, Math.round(cfg.bands)))
    for (let b = 0; b < bands; b++) {
      const count = Math.max(0, Math.round(cfg.perBand[b]?.count ?? 0))
      for (let n = 0; n < count && this.zones.length < MAX_ZONES; n++) {
        const dir = randomDir()
        // Travel axis perpendicular to dir so the angular speed stays meaningful.
        const axis = randomDir().sub(dir.clone().multiplyScalar(dir.dot(randomDir())))
        if (axis.lengthSq() < 1e-4) axis.set(0, 1, 0)
        axis.normalize()
        this.zones.push({ dir, axis, speedMul: 0.5 + Math.random() * 1.5, band: b })
      }
    }
  }

  /** Advance the zones and publish the uniforms. Call only when enabled. */
  update(cfg: ZoneReactionConfig, bandPulse: number[], dt: number, beatPeriod: number) {
    const sig = signature(cfg)
    if (sig !== this.sig) {
      this.sig = sig
      this.rebuild(cfg)
    }
    // Travel speed (rad/s). With BPM sync it's locked to the beat; otherwise it's
    // the raw slider. Either way the Speed slider scales it (multiplier in BPM
    // mode, default 1 = unchanged), so Speed is never inert.
    const base = cfg.bpmSync
      ? (Math.PI / Math.max(0.1, beatPeriod)) * 0.5 * cfg.speed
      : cfg.speed * 0.4

    const u = this.uniforms
    u.uZoneEnabled.value = 1
    u.uZoneShowShapes.value = cfg.showShapes === false ? 0 : 1
    u.uZoneCount.value = this.zones.length
    for (let i = 0; i < this.zones.length; i++) {
      const z = this.zones[i]
      const band = cfg.perBand[z.band]
      const w = (cfg.speedRandom ? z.speedMul : 1) * base
      z.dir.applyAxisAngle(z.axis, w * dt).normalize()
      ;(u.uZoneCenter.value[i] as THREE.Vector3).copy(z.dir)
      u.uZoneBand.value[i] = z.band
      u.uZoneShape.value[i] = SHAPE_ID[band?.shape ?? 'circle']
      u.uZoneRadius.value[i] = band?.size ?? 0.3
    }
    for (let k = 0; k < 4; k++) u.uBandPulse.value[k] = bandPulse[k] ?? 0
  }

  /** Turn the zone effect off in the shaders (primitive behaves as usual). */
  disable() {
    this.uniforms.uZoneEnabled.value = 0
  }

  /** Load one image per band (index 0..3) into the zone image samplers. */
  setImages(urls: (string | null)[]) {
    const samplers = [
      this.uniforms.uZoneImage0,
      this.uniforms.uZoneImage1,
      this.uniforms.uZoneImage2,
      this.uniforms.uZoneImage3,
    ]
    for (let i = 0; i < 4; i++) {
      const url = urls[i] ?? null
      if (!url) {
        this.uniforms.uZoneImageActive.value[i] = 0
        continue
      }
      this.loader.load(url, (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace
        tex.minFilter = THREE.LinearFilter
        tex.magFilter = THREE.LinearFilter
        tex.generateMipmaps = false
        samplers[i].value = tex
        this.uniforms.uZoneImageActive.value[i] = 1
      })
    }
  }
}
